import React, { useMemo, useState } from 'react';
import { MapContainer, TileLayer, CircleMarker, Tooltip, Polyline } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import type {
    IExpansionMapProps,
    IMapMarker,
    IMapEntity,
    MapEntityType,
    MapEntityStatus,
} from '../types';

// Visual tokens for enriched entities
const typeColors: Record<MapEntityType, { dot: string; halo: string }> = {
    artist: { dot: 'bg-green-500', halo: 'bg-green-500' },
    brand: { dot: 'bg-blue-500', halo: 'bg-blue-500' },
    office: { dot: 'bg-zinc-400', halo: 'bg-zinc-400' },
    event: { dot: 'bg-purple-500', halo: 'bg-purple-500' },
};

const statusRing: Record<MapEntityStatus, string> = {
    active: 'ring-2 ring-white/70 dark:ring-black/60',
    negotiation: 'ring-2 ring-yellow-400 ring-offset-2 ring-offset-black/30 dark:ring-offset-white/10',
    planned: 'ring-2 ring-zinc-300 dark:ring-zinc-600 animate-pulse',
    risk: 'ring-2 ring-red-500 ring-offset-2 ring-offset-black/30',
};

// Optional enrichment for legacy markers (by id)
const DEFAULT_ENRICHMENT: Record<string, Partial<IMapEntity>> = {
    'ny-artist': { status: 'active', metrics: { momentum: 78, reach: 34500000 }, media: { externalUrl: 'https://open.spotify.com/artist/0Y5tJX1MQlPlqiwlOH1tJY' }, relatedIds: ['ny-brand'], latlng: { lat: 40.7505, lng: -73.9934 } },
    'ny-office': { status: 'active', metrics: { momentum: 35 }, latlng: { lat: 40.7115, lng: -74.0123 } },
    'ny-brand': { status: 'negotiation', metrics: { momentum: 65, value: 2500000 }, media: { externalUrl: 'https://www.nike.com' }, relatedIds: ['ny-artist'], latlng: { lat: 40.7233, lng: -73.9980 } },
    'pa-office': { status: 'active', metrics: { momentum: 22 }, latlng: { lat: 48.8720, lng: 2.3350 } },
    'pa-brand': { status: 'planned', metrics: { momentum: 40, value: 1800000 }, latlng: { lat: 48.8667, lng: 2.3140 } },
    'pa-event': { status: 'planned', metrics: { momentum: 28 }, latlng: { lat: 48.8606, lng: 2.3376 } },
    'lv-artist': { status: 'active', metrics: { momentum: 84, reach: 12100000 }, media: { externalUrl: 'https://open.spotify.com/artist/6vWDO969PvNqNYHIOW5v0m' }, relatedIds: ['lv-brand'], latlng: { lat: 36.1029, lng: -115.1746 } },
    'lv-brand': { status: 'negotiation', metrics: { momentum: 51, value: 3200000 }, relatedIds: ['lv-artist'], latlng: { lat: 36.1030, lng: -115.1719 } },
    'mi-office': { status: 'active', metrics: { momentum: 30 }, latlng: { lat: 25.7740, lng: -80.1900 } },
    'mi-artist': { status: 'risk', metrics: { momentum: 33, reach: 8900000 }, relatedIds: ['mi-brand'], latlng: { lat: 25.7880, lng: -80.1400 } },
    'mi-brand': { status: 'active', metrics: { momentum: 57, value: 900000 }, relatedIds: ['mi-artist'], latlng: { lat: 25.7800, lng: -80.2000 } },
};

// City defaults for real map (center/zoom) when not provided in data
const CITY_DEFAULTS: Record<string, { center: { lat: number; lng: number }; zoom: number }> = {
    new_york: { center: { lat: 40.7128, lng: -74.006 }, zoom: 12 },
    paris: { center: { lat: 48.8566, lng: 2.3522 }, zoom: 12 },
    las_vegas: { center: { lat: 36.1699, lng: -115.1398 }, zoom: 12 },
    miami: { center: { lat: 25.7617, lng: -80.1918 }, zoom: 12 },
};

// Build enriched entities either from provided entities or legacy markers
const buildEntities = (markers?: IMapMarker[], entities?: IMapEntity[]): IMapEntity[] => {
    if (entities && entities.length) return entities;
    if (!markers) return [];
    return markers.map(m => {
        const e = DEFAULT_ENRICHMENT[m.id] || {};
        return {
            id: m.id,
            type: m.type as MapEntityType,
            name: m.name,
            status: (e.status ?? 'active') as MapEntityStatus,
            coords: {
                topPct: parseFloat(m.position.top.replace('%', '')) || 0,
                leftPct: parseFloat(m.position.left.replace('%', '')) || 0,
            },
            ...(e.metrics ? { metrics: e.metrics } : {}),
            ...(e.media ? { media: e.media } : {}),
            relatedIds: e.relatedIds ?? [],
        } as IMapEntity;
    });
};

// (removed filter chips UI)

const Marker = ({ entity, index, onClick, selected }: { entity: IMapEntity; index: number; onClick: () => void; selected: boolean }) => {
    const colors = typeColors[entity.type];
    const momentum = Math.max(0, Math.min(100, entity.metrics?.momentum ?? 0));
    const haloScale = 1 + momentum / 100; // 1..2
    const ringCls = statusRing[entity.status];
    return (
        <div
            className="absolute -translate-x-1/2 -translate-y-1/2"
            style={{ top: `${entity.coords.topPct}%`, left: `${entity.coords.leftPct}%` }}
        >
            <button
                type="button"
                onClick={onClick}
                className={`relative w-9 h-9 rounded-full grid place-items-center focus:outline-none ${ringCls} ${selected ? 'scale-110' : ''}`}
                style={{ animationDelay: `${index * 80}ms` }}
                aria-label={entity.name}
                title={entity.name}
            >
                <div className={`absolute inset-0 rounded-full ${colors.halo} opacity-30 blur-md`} style={{ transform: `scale(${haloScale})` }} />
                <div className={`absolute inset-0 rounded-full ${colors.halo} opacity-30 pulse-marker`} />
                <div className={`w-3.5 h-3.5 rounded-full ${colors.dot} z-10 border-2 border-black/50`} />
            </button>
        </div>
    );
};

const LegendV2: React.FC<{ allTypes: { key: MapEntityType; label: string }[] } > = ({ allTypes }) => (
    <div className="absolute bottom-3 right-3 bg-white/5 dark:bg-black/40 backdrop-blur-sm p-2 rounded-lg flex flex-col gap-1 border border-zinc-200/20 dark:border-zinc-700/50 z-[10000] pointer-events-none">
        {allTypes.map(({ key, label }) => (
            <div key={key} className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${typeColors[key].dot}`}></div>
                <span className="text-[11px] text-zinc-700 dark:text-zinc-300">{label}</span>
            </div>
        ))}
    </div>
);

export const ExpansionMap = ({ data }: IExpansionMapProps) => {
    const safeData: any = data || {};
    const cityKeys = Object.keys(safeData);
    if (cityKeys.length === 0) {
        return (
            <div className="bg-surface p-5 rounded-xl card border border-default text-sm text-muted">
                Sem dados de mapa para exibir.
            </div>
        );
    }
    const [activeCityKey, setActiveCityKey] = useState<string>(cityKeys[0] as string);
    const activeCityData = (safeData as Record<string, any>)[activeCityKey] || {};

    // Interactive state
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const entities: IMapEntity[] = useMemo(() => buildEntities(activeCityData?.markers, (activeCityData as any)?.entities), [activeCityData]);
    const selected = useMemo(() => entities.find(e => e.id === selectedId) || null, [entities, selectedId]);

    const allTypes: { key: MapEntityType; label: string }[] = [
        { key: 'artist', label: 'Artista' },
        { key: 'brand', label: 'Marca' },
        { key: 'office', label: 'Escritorio' },
        { key: 'event', label: 'Evento' },
    ];

    const DetailPanel = () => {
        if (!selected) return null;
        return (
            <aside className="absolute top-3 right-3 w-80 max-w-[85vw] bg-surface border border-default rounded-lg shadow-xl p-4 z-30">
                <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                        <p className="text-xs text-muted">{activeCityData.name}</p>
                        <h4 className="font-bold text-zinc-900 dark:text-white truncate">{selected.name}</h4>
                        <p className="text-xs text-muted mt-1">Tipo: {selected.type} • Status: {selected.status}</p>
                    </div>
                    <button onClick={() => setSelectedId(null)} className="text-zinc-500 hover:text-zinc-900 dark:hover:text-white" aria-label="Fechar"><i className="ph ph-x" /></button>
                </div>
                <div className="mt-3 grid grid-cols-3 gap-2">
                    <div className="bg-subtle border border-default rounded-md p-2 text-center">
                        <p className="text-[10px] text-muted">Momentum</p>
                        <p className="text-sm font-bold">{selected.metrics?.momentum ?? 0}</p>
                    </div>
                    <div className="bg-subtle border border-default rounded-md p-2 text-center">
                        <p className="text-[10px] text-muted">Valor</p>
                        <p className="text-sm font-bold">{selected.metrics?.value ? `R$ ${(selected.metrics.value/1_000_000).toFixed(1)}M` : '-'}</p>
                    </div>
                    <div className="bg-subtle border border-default rounded-md p-2 text-center">
                        <p className="text-[10px] text-muted">Alcance</p>
                        <p className="text-sm font-bold">{selected.metrics?.reach ? `${(selected.metrics.reach/1_000_000).toFixed(1)}M` : '-'}</p>
                    </div>
                </div>
                <div className="mt-3 flex gap-2">
                    <a href={selected.media?.externalUrl || '#'} target="_blank" rel="noreferrer" className="px-3 py-1.5 rounded-md bg-[#1DB954] text-black text-sm font-semibold disabled:opacity-60" aria-disabled={!selected.media?.externalUrl}>Abrir externo</a>
                    <a href="/admin" className="px-3 py-1.5 rounded-md bg-zinc-900 text-white dark:bg-white/10 dark:text-zinc-100 text-sm">Abrir Admin</a>
                </div>
            </aside>
        );
    };

    const Connections = () => {
        if (!selected) return null;
        const size = { w: 1000, h: 1000 };
        const s = selected;
        const sx = (s.coords.leftPct / 100) * size.w;
        const sy = (s.coords.topPct / 100) * size.h;
        const peers = (s.relatedIds || []).map(id => entities.find(e => e.id === id)).filter(Boolean) as IMapEntity[];
        if (!peers.length) return null;
        return (
            <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox={`0 0 ${size.w} ${size.h}`}>
                {peers.map((p) => {
                    const px = (p.coords.leftPct / 100) * size.w;
                    const py = (p.coords.topPct / 100) * size.h;
                    const mx = (sx + px) / 2 + (py - sy) * 0.05;
                    const my = (sy + py) / 2 + (sx - px) * 0.05;
                    return (
                        <path key={p.id} d={`M ${sx} ${sy} Q ${mx} ${my} ${px} ${py}`} stroke="#1DB954" strokeOpacity="0.65" strokeWidth="3" fill="none" />
                    );
                })}
            </svg>
        );
    };

    return (
        <div className="bg-surface p-5 rounded-xl card fade-in border border-default" style={{ animationDelay: '0.4s' }}>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4">
                <h3 className="text-lg xl:text-xl font-bold text-zinc-900 dark:text-white mb-3 sm:mb-0">Mapa de Expansao Global</h3>
                <div className="flex items-center gap-2 bg-zinc-200 dark:bg-zinc-800 p-1 rounded-full">
                            {cityKeys.map((key) => (
                        <button
                            key={key}
                            onClick={() => { setActiveCityKey(key); setSelectedId(null); }}
                            className={`px-4 py-1.5 text-sm font-semibold rounded-full transition-colors duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-green-500 ${activeCityKey === key ? 'bg-green-500 text-black' : 'text-zinc-600 dark:text-zinc-300 hover:bg-zinc-300 dark:hover:bg-zinc-700'}`}
                                >
                            {(safeData as Record<string, any>)[key]?.name || key}
                        </button>
                    ))}
                </div>
            </div>

            <div
                key={activeCityKey}
                className="relative w-full h-80 md:h-96 rounded-lg overflow-hidden border border-default bg-zinc-900"
            >
                {(() => {
                    const cityDefaults = CITY_DEFAULTS[activeCityKey] || CITY_DEFAULTS['new_york'];
                    const useRealMap = Boolean((data as any)[activeCityKey]?.center || cityDefaults);
                    if (!useRealMap) {
                        return (
                            <>
                                <img
                                    src={activeCityData.mapImage}
                                    alt={`Mapa de ${activeCityData.name}`}
                                    className="absolute inset-0 w-full h-full object-cover opacity-40 dark:opacity-30 transition-all duration-500"
                                    key={`${activeCityKey}-img`}
                                    loading="lazy"
                                    onError={(e) => { (e.currentTarget as HTMLImageElement).src = 'https://placehold.co/800x600?text=Mapa'; }}
                                />
                                <div className="radar-grid"></div>
                                <div className="radar-sweep"></div>
                                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
                                <Connections />
                                {entities.length > 0 ? (
                                    entities.map((e, idx) => (
                                        <Marker key={e.id} entity={e} index={idx} selected={selectedId === e.id} onClick={() => setSelectedId(e.id)} />
                                    ))
                                ) : (
                                    ((activeCityData?.markers || []) as IMapMarker[]).map((marker, index) => (
                                        <LegacyMarker key={marker.id} marker={marker} index={index} />
                                    ))
                                )}
                            </>
                        );
                    }
                    const center = (data as any)[activeCityKey]?.center || cityDefaults!.center;
                    const zoom = (data as any)[activeCityKey]?.zoom || cityDefaults!.zoom;
                    const display = entities.map(e => ({
                        entity: e,
                        ll: e.latlng || (DEFAULT_ENRICHMENT[e.id]?.latlng as any) || null,
                    })).filter(x => x.ll);
                    return (
                        <MapContainer center={center} zoom={zoom} className="absolute inset-0 w-full h-full" preferCanvas>
                            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap" />
                            {/* Heat-like halos (additional -10%: total ≈ -23.5%) */}
                            {display.map(({ entity, ll }) => {
                                const momentum = Math.max(0, Math.min(100, entity.metrics?.momentum ?? 0));
                                const haloRadius = (40 + momentum * 0.6) * 0.765; // scaled
                                return (
                                    <CircleMarker key={`halo-${entity.id}`} center={ll as any} radius={haloRadius} pathOptions={{ color: 'transparent', fillColor: '#22c55e', fillOpacity: 0.12 }} />
                                );
                            })}
                            {/* Primary points (additional -10%) */}
                            {display.map(({ entity, ll }) => {
                                const momentum = Math.max(0, Math.min(100, entity.metrics?.momentum ?? 0));
                                const radius = (8 + momentum * 0.12) * 0.765;
                                const color = entity.type === 'artist' ? '#22c55e' : entity.type === 'brand' ? '#3b82f6' : entity.type === 'office' ? '#a1a1aa' : '#a855f7';
                                return (
                                    <CircleMarker key={`pt-${entity.id}`} center={ll as any} radius={radius} pathOptions={{ color: '#000', weight: 2, fillColor: color, fillOpacity: 0.9 }} eventHandlers={{ click: () => setSelectedId(entity.id) }}>
                                        <Tooltip direction="top" offset={[0, -6]} opacity={1} permanent={false} className="!bg-zinc-900 !text-white !border !border-zinc-700">
                                            <div className="text-[10px] font-semibold">{entity.name}</div>
                                        </Tooltip>
                                    </CircleMarker>
                                );
                            })}
                            {/* Connections for selected */}
                            {(() => {
                                if (!selected) return null;
                                const sll = (selected.latlng || (DEFAULT_ENRICHMENT[selected.id]?.latlng as any));
                                if (!sll) return null;
                                const peers = (selected.relatedIds || []).map(id => entities.find(e => e.id === id)).filter(Boolean) as IMapEntity[];
                                return peers.map(p => {
                                    const pll = (p.latlng || (DEFAULT_ENRICHMENT[p.id]?.latlng as any));
                                    if (!pll) return null;
                                    return <Polyline key={`ln-${p.id}`} positions={[sll as any, pll as any]} pathOptions={{ color: '#1DB954', weight: 3, opacity: 0.7 }} />;
                                });
                            })()}
                        </MapContainer>
                    );
                })()}

                {/* Overlays: legend and details */}
                <LegendV2 allTypes={allTypes} />
                <DetailPanel />
                {/* Quick indicators (top-left, 15% smaller, fixed z-index) */}
                {(() => {
                    const avgMomentum = entities.length ? Math.round(entities.reduce((a, b) => a + (b.metrics?.momentum ?? 0), 0) / entities.length) : 0;
                    const engagement = avgMomentum > 70 ? 'Alto' : avgMomentum > 40 ? 'Medio' : 'Baixo';
                    const plannedEvents = entities.filter(e => e.type === 'event' && e.status === 'planned').length;
                    const offices = entities.filter(e => e.type === 'office').length;
                    return (
                        <div className="absolute top-3 left-3 bg-white/60 dark:bg-black/50 backdrop-blur-sm px-2 py-1.5 rounded-md text-xs border border-default shadow-sm z-[10000] pointer-events-none text-muted">
                            <span className="font-semibold">Shows Futuros:</span> {plannedEvents}
                            <span className="mx-2">|</span>
                            <span className="font-semibold">Escritorios:</span> {offices}
                            <span className="mx-2">|</span>
                            <span className="font-semibold">Engajamento:</span> {engagement}
                        </div>
                    );
                })()}
            </div>
        </div>
    );
};

// Simple legacy marker for fallback when only basic markers are present
const LegacyMarker = ({ marker, index }: { marker: IMapMarker; index: number }) => {
    const [isTooltipVisible, setIsTooltipVisible] = useState(false);
    const circle = (() => {
        switch (marker.type) {
            case 'artist': return 'bg-green-500';
            case 'brand': return 'bg-blue-500';
            case 'office': return 'bg-zinc-400';
            default: return 'bg-zinc-400';
        }
    })();
    return (
        <div
            className="absolute -translate-x-1/2 -translate-y-1/2 group"
            style={{ top: marker.position.top, left: marker.position.left }}
        >
            <button
                type="button"
                className="relative flex items-center justify-center w-8 h-8 cursor-pointer fade-in rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-zinc-900 focus:ring-green-500"
                style={{ animationDelay: `${index * 100}ms` }}
                onMouseEnter={() => setIsTooltipVisible(true)}
                onMouseLeave={() => setIsTooltipVisible(false)}
                onFocus={() => setIsTooltipVisible(true)}
                onBlur={() => setIsTooltipVisible(false)}
                aria-label={marker.name}
            >
                <div className={`w-3 h-3 rounded-full ${circle} z-10 border-2 border-black/50`}></div>
                <div className={`absolute w-full h-full rounded-full ${circle} opacity-50 pulse-marker`}></div>
            </button>
            <div
                className={`absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 bg-zinc-900 border border-zinc-700 text-white text-xs font-semibold rounded-md shadow-lg transition-all duration-300 pointer-events-none whitespace-nowrap z-30 ${isTooltipVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}`}
                role="tooltip"
            >
                {marker.name}
            </div>
        </div>
    );
};
